/**
 * W-2 · Vue d'ensemble.
 *
 * Les libellés et les infobulles des tuiles reprennent MOT POUR MOT les
 * définitions de `DATA-MODEL.md` §3. C'est la seule protection contre le
 * malentendu classique : « connectés » n'est pas un nombre de comptes, c'est le
 * nombre de sessions ouvertes ayant émis un battement depuis moins de 5 minutes.
 *
 * Passe bento (2026-08-24) — trois ajouts, et leurs limites :
 *  · UNE tuile pleine par grille : « athlètes connectés », le chiffre autour
 *    duquel la garde s'organise ;
 *  · les tuiles qui ont un détail ailleurs portent la flèche d'ouverture ;
 *  · les écarts (« +22 % vs période précédente ») sont CALCULÉS, par un second
 *    appel sur la période précédente de même durée. Ils ne sont posés que sur
 *    les chiffres réellement bornés par la période — « nouveaux arrivés »,
 *    « visites », « durée d'une visite ». « Athlètes équipés » est un cumul
 *    depuis l'ouverture du service (il ne peut que monter) et les « actifs
 *    24 h / 7 j / 30 j » sont des fenêtres glissantes ancrées à MAINTENANT :
 *    leur comparer une période antérieure produirait un écart nul ou trompeur.
 *
 * Bloc 2 UI/UX (2026-10-14) : au premier regard, trois chiffres (le temps réel)
 * puis trois (ce que la période a changé : équipés, nouveaux, visites). Les fenêtres glissantes et les
 * retours, et la durée de visite, sont repliés derrière « Plus de chiffres » — ils ne changent pas
 * d'une heure à l'autre, et huit tuiles de même poids faisaient perdre le
 * chiffre qui compte. « Reviennent » est coupée en deux : « 0 % / 0 % » se
 * lisait comme une fraction.
 */

import { useState } from 'react';
import { api } from '../lib/api';
import type { TimeseriesMetric } from '../lib/api';
import { previousRange, rangeKey } from '../lib/range';
import type { Range } from '../lib/range';
import { useApi, useTick } from '../lib/useApi';
import { formatDay, formatDateTime, formatDuration, formatInt, formatPercent, formatSince } from '../lib/format';
import { Async, Banner, Card, Freshness, Kpi } from '../components/ui';
import { LineChart, Sparkline } from '../charts';
import { presetRange } from '../lib/range';

/** Période de rafraîchissement des SEULES données temps réel. */
const LIVE_REFRESH_MS = 30_000;

const ACTIVITY_METRICS: { key: TimeseriesMetric; label: string; hint: string }[] = [
  { key: 'active_users', label: 'Actifs', hint: 'Utilisateurs distincts ayant émis un événement dans le seau.' },
  { key: 'sessions', label: 'Sessions', hint: 'Sessions démarrées dans le seau.' },
  { key: 'events', label: 'Événements', hint: 'Volume brut d’événements ingérés.' },
  { key: 'connected', label: 'Connectés', hint: 'Sessions ouvertes avec un battement de moins de 5 minutes.' },
];

/**
 * Écart relatif entre deux périodes.
 *
 * `undefined` — donc AUCUN écart affiché — dans les deux cas où le pourcentage
 * mentirait : période précédente pas encore chargée, ou base à zéro (passer de
 * 0 à 12 n'est pas « +1 200 % », c'est un démarrage).
 */
function deltaOf(current: number | undefined, past: number | undefined): number | undefined {
  if (current === undefined || past === undefined || past <= 0) return undefined;
  return (current - past) / past;
}

/**
 * Enveloppe le delta pour `<Kpi>` — `undefined` quand il n'y a rien à dire.
 * `upIsGood` dit au composant s'il a le droit de colorer l'écart ; `null` le
 * laisse en gris, pour les chiffres dont aucune direction n'est une bonne
 * nouvelle en soi.
 */
function trendOf(
  delta: number | undefined,
  upIsGood: boolean | null = true,
): { delta: number; label: string; upIsGood: boolean | null } | undefined {
  return delta === undefined ? undefined : { delta, label: 'vs période précédente', upIsGood };
}

export function OverviewPage({ range }: { range: Range }): React.ReactElement {
  const [metric, setMetric] = useState<TimeseriesMetric>('active_users');
  const now = useTick(15_000);

  // Deux appels à `/metrics/overview` — c'est délibéré :
  //  - l'un porte la période choisie et ne bouge qu'au changement de période ;
  //  - l'autre, sans paramètre (donc sur les dernières 24 h), est rappelé
  //    toutes les 30 s et n'alimente QUE le bloc temps réel. Rafraîchir toute
  //    la page toutes les 30 s ferait clignoter des chiffres qui, eux, ne
  //    bougent pas à cette cadence.
  const scoped = useApi((s) => api.overview({ from: range.from, to: range.to }, s), rangeKey(range));
  // Période précédente de même durée : la seule base honnête pour un écart.
  const before = previousRange(range);
  const previous = useApi(
    (s) => api.overview({ from: before.from, to: before.to }, s),
    `prev|${rangeKey(before)}`,
  );
  const live = useApi((s) => api.overview({}, s), 'live', { refreshMs: LIVE_REFRESH_MS });

  const activity = useApi(
    (s) => api.timeseries(metric, { from: range.from, to: range.to }, range.bucket, s),
    `${metric}|${rangeKey(range)}`,
  );

  // Mini-courbe du bloc temps réel : toujours 24 h au pas horaire, quelle que
  // soit la période affichée ailleurs — c'est une jauge d'instant, pas un
  // graphique de période.
  const connected24h = useApi(
    (s) => api.timeseries('connected', presetRange('24h'), 'hour', s),
    'connected-24h',
    { refreshMs: LIVE_REFRESH_MS },
  );

  const formatX = range.bucket === 'hour' ? formatDateTime : formatDay;
  const metricMeta = ACTIVITY_METRICS.find((m) => m.key === metric);

  // Avant les premières ouvertures de l'application, chaque carte afficherait
  // son propre cadre « aucune donnée » : six fois, on croit à une panne. Un seul
  // message, ici, dit la vraie situation.
  const nothingYet = scoped.data != null && scoped.data.users.total === 0;

  return (
    <div className="stack">
      {nothingYet ? (
        <Banner tone="info" title="L'application n'a pas encore envoyé de données">
          Les chiffres arriveront dès les premières ouvertures de l'application par les athlètes. Rien n'est en panne.
        </Banner>
      ) : null}
      {/* ─── Temps réel ─────────────────────────────────────────────────── */}
      <Card
        title="Temps réel"
        hint="Ces trois chiffres décrivent l’instant présent. Ils se mettent à jour tout seuls, quelle que soit la période choisie ci-dessus."
        action={
          <Freshness
            state={live}
            sinceLabel={live.updatedAt ? `à jour ${formatSince(new Date(live.updatedAt).toISOString(), now)}` : 'pas encore chargé'}
          />
        }
      >
        <Async state={live}>
          {(data) => (
            <div className="grid grid-kpi">
              <Kpi
                featured
                href="#/users"
                label="Athlètes connectés"
                value={formatInt(data.live.connected)}
                live
                livePulsing={data.live.connected > 0}
                hint={`Ont l’application ouverte en ce moment. Un athlète disparaît de ce compteur ${data.live.windowMin} minutes après avoir fermé l’application.`}
              >
                {connected24h.data ? (
                  <Sparkline
                    label="Sur les dernières 24 heures"
                    points={connected24h.data.points}
                    /* Sur la tuile pleine, le vert de marque devient illisible :
                       la courbe passe en blanc voilé, comme les autres textes. */
                    color="rgba(255, 255, 255, 0.9)"
                  />
                ) : null}
              </Kpi>
              {/*
                Deux fraîcheurs se superposent ici, et les confondre est le
                piège de cette tuile :
                  · l'âge du plus vieux POINT GPS dans l'instantané ;
                  · l'âge de l'INSTANTANÉ lui-même.
                Si le job d'échantillonnage meurt, le serveur continue de servir
                le dernier instantané connu — le compteur se figerait en ayant
                l'air temps réel. On affiche donc « chiffre périmé » plutôt que
                de laisser croire à une flotte à l'arrêt : zéro voudrait dire
                « aucun bus », alors que la vérité est « on ne sait plus ».
              */}
              <Kpi
                href="#/network"
                label="Bus qui communiquent"
                value={data.fleet.stale ? '?' : formatInt(data.fleet.vehiclesOnline)}
                hint={
                  data.fleet.stale
                    ? `Chiffre périmé : aucune position reçue depuis ${formatDuration(data.fleet.snapshotAgeSec)}. ` +
                      `Le dernier décompte connu était de ${formatInt(data.fleet.vehiclesOnline)} bus. ` +
                      `Prévenir l’équipe technique : on ne sait plus combien de bus roulent réellement.`
                    : `Bus dont la position arrive bien jusqu’à l’application, sur ` +
                      `${formatInt(data.fleet.linesCovered)} ligne(s). ` +
                      `La position la plus ancienne date de ${formatDuration(data.fleet.oldestFixAgeSec)}.`
                }
              />
              <Kpi
                href="#/incidents"
                label="Signalements à traiter"
                value={formatInt(data.incidents.open)}
                hint={`Envoyés par les athlètes et pas encore pris en charge. ${formatInt(data.incidents.resolved24h)} ont été réglés depuis hier.`}
              />
            </div>
          )}
        </Async>
      </Card>

      {/* ─── KPI de la période ──────────────────────────────────────────── */}
      <Card
        title={`Indicateurs · ${range.label}`}
        hint="Ce que la période a changé. Chaque chiffre est expliqué sous son intitulé."
        action={<Freshness state={scoped} sinceLabel={scoped.updatedAt ? 'chargé' : 'pas encore chargé'} />}
      >
        <Async state={scoped}>
          {(data) => (
            <>
            <div className="grid grid-kpi">
              <Kpi
                label="Athlètes équipés"
                value={formatInt(data.users.total)}
                hint="Ont ouvert l’application au moins une fois depuis le début. Réinstaller l’application ne compte pas une personne de plus."
              />
              <Kpi
                label="Nouveaux arrivés"
                value={formatInt(data.users.new)}
                trend={trendOf(deltaOf(data.users.new, previous.data?.users.new))}
                hint="Ont ouvert l’application pour la toute première fois pendant la période."
              />
              <Kpi
                label="Visites"
                value={formatInt(data.sessions.count)}
                trend={trendOf(deltaOf(data.sessions.count, previous.data?.sessions.count))}
                hint="Nombre de fois où l’application a été ouverte pendant la période."
              />
            </div>
            <details className="fold fold-inset">
              <summary>Plus de chiffres · durée d’une visite, actifs sur 24 h, 7 jours et 30 jours, retours</summary>
              <div className="fold-body grid grid-kpi">
                <Kpi
                  label="Durée d’une visite"
                  value={formatDuration(data.sessions.medianDurationSec)}
                  /* Écart en gris : une visite plus courte peut signifier « l'athlète
                     a trouvé son horaire plus vite » aussi bien que « il a renoncé ». */
                  trend={trendOf(
                    deltaOf(data.sessions.medianDurationSec, previous.data?.sessions.medianDurationSec),
                    null,
                  )}
                  hint="Durée d’une visite type. La moitié des visites sont plus courtes, l’autre moitié plus longues."
                />
                <Kpi
                  label="Actifs aujourd’hui"
                  value={formatInt(data.users.active24h)}
                  hint="Ont utilisé l’application au cours des 24 dernières heures, pas depuis minuit."
                />
                <Kpi label="Actifs cette semaine" value={formatInt(data.users.active7d)} hint="Ont utilisé l’application au cours des 7 derniers jours." />
                <Kpi label="Actifs ce mois-ci" value={formatInt(data.users.active30d)} hint="Ont utilisé l’application au cours des 30 derniers jours." />
                <Kpi
                  label="Reviennent le lendemain"
                  value={formatPercent(data.retention.d1)}
                  hint="Part des athlètes qui rouvrent l’application le lendemain de leur première visite."
                />
                <Kpi
                  label="Reviennent une semaine après"
                  value={formatPercent(data.retention.d7)}
                  hint="Part des athlètes qui rouvrent l’application sept jours après leur première visite."
                />
              </div>
            </details>
            </>
          )}
        </Async>
      </Card>

      {/* ─── Courbe d'activité, repliée (décision du porteur, 2026-10-14) ──── */}
      <details className="fold">
        <summary>Évolution dans le temps · {metricMeta?.label ?? metric} · {range.label}</summary>
        <div className="fold-body stack" style={{ gap: 12 }}>
          <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <p className="small muted" style={{ margin: 0 }}>{metricMeta?.hint}</p>
            <div className="seg" role="group" aria-label="Métrique affichée">
              {ACTIVITY_METRICS.map((m) => (
                <button key={m.key} type="button" aria-pressed={metric === m.key} onClick={() => setMetric(m.key)}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <Async state={activity}>
            {(data) => (
              <LineChart
                title={`${metricMeta?.label ?? metric} · ${range.label}`}
                formatX={formatX}
                series={[
                  {
                    key: data.metric,
                    label: metricMeta?.label ?? data.metric,
                    color: 'var(--c-blue)',
                    points: data.points,
                  },
                ]}
                height={260}
              />
            )}
          </Async>
        </div>
      </details>
    </div>
  );
}
